import { VersionBadge } from './VersionBadge';
import { useState } from 'react';
import { GenerationReadError } from './GenerationReadError';
import { generationStatusLabels, generationTime } from './generationCopy';
import { GENERATION_STATUSES, GenerationParams } from './generationTypes';
import { useCurrentMap, useGenerationHistory } from './useGenerationQueries';

export function GenerationHistory({ select }: { select: (id: string) => void }) {
  const [params, setParams] = useState<GenerationParams>({ page: 1, per_page: 20, sort: '-created_at' });
  const query = useGenerationHistory(params);
  const current = useCurrentMap();
  const knownCurrent = current.isSuccess ? current.data : null;
  const page = query.isError ? undefined : query.data;
  const changeStatus = (value: string): void => {
    const status = GENERATION_STATUSES.find((candidate) => candidate === value);
    setParams((previous) => ({ ...previous, page: 1, status }));
  };

  return (
    <section className="map-generations__panel" aria-label="История генераций">
      <h2>История генераций</h2>
      <div className="map-generations__controls">
        <label>
          Статус запуска
          <select value={params.status ?? ''} onChange={(event) => changeStatus(event.target.value)}>
            <option value="">Все статусы</option>
            {GENERATION_STATUSES.map((status) => (
              <option key={status} value={status}>{generationStatusLabels[status]}</option>
            ))}
          </select>
        </label>
        <label>
          Порядок запусков
          <select value={params.sort} onChange={(event) => { const sort = event.target.value === 'created_at' ? 'created_at' : '-created_at'; setParams((previous) => ({ ...previous, page: 1, sort })); }}>
            <option value="-created_at">Сначала новые</option>
            <option value="created_at">Сначала старые</option>
          </select>
        </label>
        <button className="workspace-secondary-button" type="button" disabled={query.isFetching} onClick={() => { void query.refetch(); }}>
          Обновить историю
        </button>
      </div>
      {query.isPending && <p role="status">Загрузка истории…</p>}
      {query.isError && (
        <GenerationReadError error={query.error} retry={() => { void query.refetch(); }} pending={query.isFetching} label="Повторить загрузку истории" />
      )}
      {page?.items.length === 0 && (
        <p>{page.meta.total === 0 && params.status === undefined ? 'Запусков пока нет.' : 'На этой странице запусков нет.'}</p>
      )}
      {page && (
        <>
          <ul className="map-generations__list">
            {page.items.map((job) => (
              <li key={job.id}>
                <button className="workspace-secondary-button map-generations__identifier" type="button" aria-label={`Открыть запуск ${job.id}`} onClick={() => select(job.id)}>
                  {job.id}
                </button>
                <span>{generationStatusLabels[job.status]} · {generationTime(job.created_at)}</span>
                {job.candidate_version && <VersionBadge version={job.candidate_version} />}
                {knownCurrent && job.candidate_version === knownCurrent.version && <strong>Текущая публикация</strong>}
              </li>
            ))}
          </ul>
          <nav className="map-generations__controls" aria-label="Страницы запусков">
            <p>{page.meta.page_count === 0 ? 'Страниц: 0' : `Страница ${page.meta.page} из ${page.meta.page_count}`} · Всего: {page.meta.total}</p>
            <button className="workspace-secondary-button" type="button" disabled={query.isFetching || page.meta.page <= 1} onClick={() => setParams((previous) => ({ ...previous, page: page.meta.page - 1 }))}>
              Назад
            </button>
            <button className="workspace-secondary-button" type="button" disabled={query.isFetching || page.meta.page >= page.meta.page_count} onClick={() => setParams((previous) => ({ ...previous, page: page.meta.page + 1 }))}>
              Далее
            </button>
          </nav>
        </>
      )}
    </section>
  );
}
